import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useMemo, useRef, useState } from "react";

import { aggridHeaderClass, blueButtonClass } from "src/components/common/common";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useDescText } from "src/hooks/useDescText";
import { DESC_FLD, formatDescCount } from "src/utils/descLanguage/descLanguageUtil";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import {
	CreateCellClickHandler,
	createCellCssClassRules,
	CreateCellDoubleClickHandler,
	CustomCellClickAction,
	CustomCellDoubleClickAction,
	GridCellConfig,
	useCellChangeHandler,
	useRowSelectionHandler,
} from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import {
	defaultColDef,
	getDefaultRowIndexColumnConfig,
} from "src/utils/grid/gridStyleUtils";

import {
	defaultKSK2140_R01LotDto,
	KSK2140_R01LotDto,
} from "./Dto_KSK2140_R01";
import { useKSK2140_R01Store } from "./Store_KSK2140_R01";

const COLUMN_STATE_KEY = "KSK2140_R01-lot-grid-column-state";

const GRID_CELL_CONFIG: GridCellConfig = {
	disabledCells: ["lotNo", "lotQty"],
	insertedDisabledCells: [""],
	commonDisabledCells: [""],
	pointerCells: [""],
	insertedPointerCells: [""],
	commonPointerCells: [""],
	alignLeftCells: [""],
};

interface Props {
	lotGridRef: React.RefObject<AgGridReact<KSK2140_R01LotDto> | null>;
	onSaveClick: () => void | Promise<void>;
}

export const LotTable_KSK2140_R01 = ({ lotGridRef, onSaveClick }: Props) => {
	const { renderDescText } = useDescText();
	const countTemplate = renderDescText(DESC_FLD.COUNT_DISPLAY, "{count}건");
	const noDataOverlay = `<span style="padding: 32px;">${renderDescText(DESC_FLD.NO_DATA, "조회된 데이터가 없습니다.")}</span>`;
	const {
		KSK2140_R01LotList,
		setKSK2140_R01LotList,
		selectedKSK2140_R01LotList,
		addSelectedKSK2140_R01LotList,
		removeSelectedKSK2140_R01LotList,
		initSelectedKSK2140_R01LotList,
		deletedKSK2140_R01LotList,
		updateKSK2140_R01LotList,
		lotColumnState,
		setLotColumnState,
		originalKSK2140_R01LotList,
	} = useKSK2140_R01Store();
	const [clickable, setClickable] = useState(true);
	const gridWrapperRef = useRef<HTMLDivElement>(null);
	const headerNames = useMemo<Record<string, string>>(() => ({
		rowIndex: "",
		lotQty: renderDescText(DESC_FLD.PRODUCTION_QTY, "생산량"),
		lotNo: renderDescText(DESC_FLD.LOT_NO, "LOT No"),
	}), [renderDescText]);

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
	} = useGridSettingContent({
		gridRef: lotGridRef,
		key: "valueIndex",
		gridData: KSK2140_R01LotList,
		defaultGridData: defaultKSK2140_R01LotDto,
		selectedRow: selectedKSK2140_R01LotList,
		originalGridData: originalKSK2140_R01LotList,
		updateRow: (index: number, data: any) =>
			updateKSK2140_R01LotList(index, data),
	});

	const {
		disabledInsertedColumns,
		disabledColumns,
		pointerInsertedColumns,
		pointerColumns,
		alignLeftColumns,
	} = createCellCssClassRules(GRID_CELL_CONFIG);

	const gridDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<KSK2140_R01LotDto>({
			idKey: "valueIndex",
			selectedList: selectedKSK2140_R01LotList,
			deletedList: deletedKSK2140_R01LotList,
			disabledInsertedColumns,
			disabledColumns,
			pointerInsertedColumns,
			pointerColumns,
			alignLeftColumns,
		});
		return { ...defaultColDef, cellClassRules: { ...baseRules } };
	}, [
		selectedKSK2140_R01LotList,
		deletedKSK2140_R01LotList,
		disabledInsertedColumns,
		disabledColumns,
		pointerInsertedColumns,
		pointerColumns,
		alignLeftColumns,
		createCellClassRules,
	]);

	const baseColumnDefs: ColDef<KSK2140_R01LotDto>[] = useMemo(
		() => [
			...getDefaultRowIndexColumnConfig(),
			{
				headerName: renderDescText(DESC_FLD.PRODUCTION_QTY, "생산량"),
				colId: "lotQty",
				field: "lotQty",
				width: 80,
				editable: false,
				pasteable: false,
				valueFormatter: (params: any) =>
					params.value != null ? params.value.toLocaleString() : "",
			},
			{
				headerName: renderDescText(DESC_FLD.LOT_NO, "LOT No"),
				colId: "lotNo",
				field: "lotNo",
				width: 150,
				editable: false,
			},
		],
		[renderDescText],
	);

	const columnDefs: ColDef<KSK2140_R01LotDto>[] = useMemo(() => {
		if (lotColumnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<KSK2140_R01LotDto>({
			baseColumnDefs,
			columnState: lotColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [baseColumnDefs, lotColumnState, headerNames]);

	const handleCellChange = useCellChangeHandler({
		dataList: KSK2140_R01LotList,
		originalDataList: originalKSK2140_R01LotList,
		updateRow: updateKSK2140_R01LotList,
	});

	const handleRowClick = useRowSelectionHandler({
		dataList: KSK2140_R01LotList,
		idKey: "valueIndex",
		selectedList: selectedKSK2140_R01LotList,
		deletedList: deletedKSK2140_R01LotList,
		addSelected: addSelectedKSK2140_R01LotList,
		removeSelected: removeSelectedKSK2140_R01LotList,
		clickable,
		setClickable,
	});

	const defaultEditableColumns: string[] = [];
	const cellEventHandlers = useDetailTableEvents({
		detailList: KSK2140_R01LotList as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: { enableArrowNavigation: true },
	});

	const cellClickActions: CustomCellClickAction<KSK2140_R01LotDto>[] =
		useMemo(() => [], []);
	const handleCellClick = CreateCellClickHandler(
		KSK2140_R01LotList,
		"valueIndex",
		cellClickActions,
	);

	const cellDoubleClickActions: CustomCellDoubleClickAction<KSK2140_R01LotDto>[] =
		useMemo(() => [], []);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(
		KSK2140_R01LotList,
		"valueIndex",
		cellDoubleClickActions,
	);

	const pinnedBottomRowData = useMemo(
		() => [
			{
				lotNo: formatDescCount(KSK2140_R01LotList.length, countTemplate),
				lotQty: KSK2140_R01LotList.reduce(
					(sum, item) =>
						sum +
						(deletedKSK2140_R01LotList.includes(
							item.valueIndex.toString(),
						)
							? 0
							: Number(item.lotQty) || 0),
					0,
				),
			},
		],
		[KSK2140_R01LotList, countTemplate, deletedKSK2140_R01LotList],
	);

	return (
		<div className="h-full min-h-0 flex flex-col gap-[4px]">
			<div className="w-full min-h-0 flex-1 flex flex-col">
				<GridContextWrapper<KSK2140_R01LotDto>
					gridWrapperRef={gridWrapperRef}
					gridRef={lotGridRef}
					paddedGridData={paddedGridData}
					setPaddedGridData={setKSK2140_R01LotList}
					initSelected={initSelectedKSK2140_R01LotList}
					defaultRowData={defaultKSK2140_R01LotDto}
					baseColumnDefs={columnDefs}
					setColumnState={setLotColumnState}
					localStorageKey={COLUMN_STATE_KEY}
					menuItems={undefined}
				>
					<Grid_Excel<KSK2140_R01LotDto>
						ref={lotGridRef}
						rowData={paddedGridData}
						getRowId={getRowId}
						columnDefs={columnDefs}
						overlayNoRowsTemplate={noDataOverlay}
						defaultColDef={gridDefaultColDef}
						rowClassRules={createRowClassRules<KSK2140_R01LotDto>({
							idKey: "valueIndex",
							selectedList: selectedKSK2140_R01LotList,
							deletedList: deletedKSK2140_R01LotList,
						})}
						onRowClicked={handleRowClick}
						onCellClicked={handleCellClick}
						onCellDoubleClicked={handleCellDoubleClick}
						onCellKeyDown={cellEventHandlers.handleCellKeyDown}
						onCellValueChanged={handleCellChange}
						pinnedBottomRowData={pinnedBottomRowData}
						columnStateKey={COLUMN_STATE_KEY}
						setColumnState={setLotColumnState}
					/>
				</GridContextWrapper>
			</div>
			<div className="w-full shrink-0 flex items-center gap-[4px]">
				<button
					className={`${blueButtonClass} flex-shrink-0 text-white font-semibold px-4 rounded shadow h-[32px] mb-[4px]`}
					onClick={onSaveClick}
				>
					{renderDescText(DESC_FLD.INSPECTION_RECEIPT, "검사품입고")}
				</button>
			</div>
		</div>
	);
};
